import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

function getColumnVerticalDatum(columnCatalogId: string, settings: any) {
  for (const settingId in settings) {
    const setting = settings[settingId];
    if (setting.type === "COLUMN_CATALOG_SETTINGS") {
      const columnTable = setting.columnTable;
      for (const columnId in columnTable) {
        const column = columnTable[columnId];
        if (columnId === columnCatalogId && column.subType === "STRUCTURAL" && column.verticalDatum) {
          return {
            type: column.verticalDatum.type,
            topDatumId: column.verticalDatum.topDatumId,
            topOffset: column.verticalDatum.topOffset,
            bottomDatumId: column.verticalDatum.bottomDatumId,
            bottomOffset: column.verticalDatum.bottomOffset,
          };
        }
      }
    }
  }
  return {};
}
// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_239: DocMigration = {
  maxSchemaVersion: "0.0.239",
  description: "Column placement simplified and vertical datum added",
  apply(json: any) {
    for (const settingId in json.settings) {
      const setting = json.settings[settingId];
      if (setting.type === "COLUMN_CATALOG_SETTINGS") {
        const columnTable = setting.columnTable;
        for (const columnId in columnTable) {
          const column = columnTable[columnId];
          if (column.subType === "STRUCTURAL") {
            const verticalDatum = {};

            verticalDatum["type"] = "ABSOLUTE";

            if (column.topVerticalDatumId) {
              verticalDatum["topDatumId"] = column.topVerticalDatumId;
            }
            if (column.bottomVerticalDatumId) {
              verticalDatum["bottomDatumId"] = column.bottomVerticalDatumId;
            }
            if (column.topVerticalOffset) {
              verticalDatum["topOffset"] = column.topVerticalOffset;
            }
            if (column.bottomVerticalOffset) {
              verticalDatum["bottomOffset"] = column.bottomVerticalOffset;
            }

            column["verticalDatum"] = verticalDatum;

            delete column.topVerticalDatumId;
            delete column.bottomVerticalDatumId;
            delete column.topVerticalOffset;
            delete column.bottomVerticalOffset;
          }
        }
      }
    }

    if (json.columns) {
      const allColumnIds = Object.keys(json.columns);
      allColumnIds.sort((a, b) => json.columns[a].sortIndex - json.columns[b].sortIndex);
      let sortIndex = 1;

      for (const columnId of allColumnIds) {
        const column = json.columns[columnId];
        const verticalDatumCatalog = getColumnVerticalDatum(column.catalogColumnId, json.settings);
        const verticalDatum = {};

        const columnBase = {
          entityType: "COLUMN",
          parentId: column.parentId,
          edgeId: column.edgeId,
        };
        if (column.catalogColumnId) {
          columnBase["catalogColumnId"] = column.catalogColumnId;
        }

        columnBase["paletteId"] = column.material ?? "wallExterior0";

        verticalDatum["type"] = "ABSOLUTE";

        if (verticalDatumCatalog.topDatumId) {
          verticalDatum["topDatumId"] = verticalDatumCatalog.topDatumId;
        }
        if (verticalDatumCatalog.topOffset) {
          verticalDatum["topOffset"] = verticalDatumCatalog.topOffset;
        }
        if (verticalDatumCatalog.bottomDatumId) {
          verticalDatum["bottomDatumId"] = verticalDatumCatalog.bottomDatumId;
        }
        if (verticalDatumCatalog.bottomOffset) {
          verticalDatum["bottomOffset"] = verticalDatumCatalog.bottomOffset;
        }

        columnBase["verticalDatum"] = verticalDatum;

        for (const placementId in column.placements) {
          const newColumn = { ...columnBase };
          newColumn["id"] = uid();
          newColumn["name"] = "Column Set" + " " + sortIndex.toString();
          newColumn["sortIndex"] = sortIndex;

          const placement = column.placements[placementId];

          if (!placement) {
            continue;
          }

          if (placement.isolatedAssemblyId) {
            newColumn["isolatedAssemblyId"] = placement.isolatedAssemblyId;
          }

          newColumn["horizontalDatum"] = placement.horizontalDatum;
          newColumn["lateralDatum"] = placement.lateralDatum;
          newColumn["horizontalAlignment"] = placement.horizontalAlignment;
          newColumn["horizontalOffset"] = placement.horizontalOffset;
          newColumn["horizontalOffsetNormalized"] = placement.horizontalOffsetNormalized;
          newColumn["lateralAlignment"] = placement.lateralAlignment;
          newColumn["lateralOffset"] = placement.lateralOffset;
          newColumn["lateralOffsetNormalized"] = placement.lateralOffsetNormalized;

          switch (placement.type) {
            case "PARAM":
              newColumn["placement"] = {
                type: "PARAM",
                param: placement.param,
                paramNormalized: placement.paramNormalized,
              };
              break;
            case "ARRAY":
              newColumn["placement"] = {
                type: "ARRAY",
                spacing: placement.spacing,
                shift: placement.shift,
              };
              if (placement.maxCount) {
                newColumn["placement"] = { ...newColumn["placement"], maxCount: placement.maxCount };
              }
              break;
            case "DIVISION":
              newColumn["placement"] = {
                type: "DIVISION",
                segments: placement.segments,
              };
              break;
            case "MAX_DISTANCE":
              newColumn["placement"] = {
                type: "MAX_DISTANCE",
                maxDistance: placement.maxDistance,
              };
              break;
            default:
              break;
          }
          json.columns[newColumn["id"]] = newColumn;
          sortIndex++;
        }
        delete json.columns[columnId];
      }
    }
    return json;
  },
};

export default migration_0_0_239;
